import { useCallback, useState } from 'react';

import {
  type ProviderServiceDestinationsSearchResponse,
  useProviderServiceDestinationsSearchMutation,
} from '@/services/api/generated/providerDestinationsService';
import type {
  DestinationScope,
} from '@/types/provider-destinations';

interface UseDestinationSearchResult {
  search: (query: string, includeWeb: boolean) => void;
  results: ProviderServiceDestinationsSearchResponse | null;
  isSearching: boolean;
  hasSearched: boolean;
  reset: () => void;
}

export const useDestinationSearch = (
  providerServiceId: string,
  scope: DestinationScope,
): UseDestinationSearchResult => {
  const [searchMutation, { isLoading }] =
    useProviderServiceDestinationsSearchMutation();
  const [results, setResults] =
    useState<ProviderServiceDestinationsSearchResponse | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const search = useCallback(
    async (query: string, includeWeb: boolean) => {
      try {
        const response = await searchMutation({
          providerServiceId,
          providerServiceDestinationsSearchRequest: {
            scope,
            query: query || undefined,
            include_web: includeWeb,
            include_internal_history: true,
            include_voiceai: true,
            max_results: 25,
          },
        }).unwrap();
        setResults(response);
        setHasSearched(true);
      } catch {
        setHasSearched(true);
      }
    },
    [providerServiceId, scope, searchMutation],
  );

  const reset = useCallback(() => {
    setResults(null);
    setHasSearched(false);
  }, []);

  return {
    search,
    results,
    isSearching: isLoading,
    hasSearched,
    reset,
  };
};
